import React, { useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { Upload } from "lucide-react";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import messages from "../../../assets/locale/messages";
import { UIFieldShell } from "../form";
import UIUploadProgress from "../UIUploadProgress";
import UIUploadedFile from "../UIUploadedFile";
import UIUploadSelectedFile from "../UIUploadSelectedFile/UIUploadSelectedFile";
import "./UIUpload.scss";

const DEFAULT_ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/svg+xml",
];

const DEFAULT_DROPZONE_ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
];

const DEFAULT_MAX_SIZE_BYTES = 2 * 1024 * 1024;
const DEFAULT_DROPZONE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (bytes >= 1024 * 1024) return `${bytes / (1024 * 1024)}MB`;
  if (bytes >= 1024) return `${bytes / 1024}KB`;
  return `${bytes}B`;
};

const formatMaxSizeMessage = (template, sizeLabel, sizeBytes) =>
  String(template).replace(
    "{size}",
    sizeLabel || formatBytes(sizeBytes),
  );

const fileMatchesAccept = (file, accept) => {
  if (!accept) return true;

  return accept
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean)
    .some((rule) => {
      if (rule.endsWith("/*")) {
        return file.type.startsWith(rule.replace("*", ""));
      }
      if (rule.startsWith(".")) {
        return file.name.toLowerCase().endsWith(rule.toLowerCase());
      }
      return file.type === rule;
    });
};

const getPreviewLabel = (file, preview, fallback) => {
  if (file?.name) return file.name;
  if (typeof fallback === "string" && fallback) return fallback;
  if (typeof preview === "string" && preview.startsWith("data:application/pdf")) {
    return "Selected document";
  }
  return "";
};

const UIUpload = ({
  id,
  name,
  label,
  optionalLabel,
  required,
  helperText,
  error,
  disabled,
  accept,
  acceptedTypes,
  maxSizeBytes,
  maxSizeLabel,
  invalidTypeMessage,
  invalidSizeMessage,
  uploadText,
  changeText,
  hint,
  dragHint,
  variant,
  preview,
  previewLabel,
  file,
  showRemove,
  isUploading,
  uploadProgress,
  uploadProgressLabel,
  uploadCompleted,
  successLabel,
  removeAriaLabel,
  className,
  errorAction,
  onChange,
  onError,
}) => {
  const generatedId = useId();
  const inputId = id || `ui-upload-${generatedId}`;
  const inputRef = useRef(null);
  const [localErrorKind, setLocalErrorKind] = useState(null);
  const lang = useSelector((state) => state.locale.lang);
  const { shared: sharedMessages } = messages[lang];

  const busy = disabled || isUploading;
  const isDropzone = variant !== "logo";
  const resolvedAccept =
    accept ?? (isDropzone ? ".pdf,.jpg,.jpeg,.png" : ".jpg,.jpeg,.png,.svg");
  const resolvedAcceptedTypes =
    acceptedTypes ??
    (isDropzone ? DEFAULT_DROPZONE_ACCEPTED_TYPES : DEFAULT_ACCEPTED_IMAGE_TYPES);
  const resolvedMaxSizeBytes =
    maxSizeBytes ??
    (isDropzone ? DEFAULT_DROPZONE_MAX_SIZE_BYTES : DEFAULT_MAX_SIZE_BYTES);
  const resolvedMaxSizeLabel =
    maxSizeLabel ?? (isDropzone ? "10MB" : "2MB");

  const resolvedInvalidTypeMessage =
    invalidTypeMessage ?? sharedMessages.uiUploadInvalidType;
  const resolvedInvalidSizeMessage =
    invalidSizeMessage ??
    formatMaxSizeMessage(
      sharedMessages.uiUploadMaxSize,
      resolvedMaxSizeLabel,
      resolvedMaxSizeBytes,
    );

  const localErrorMessage = useMemo(() => {
    if (localErrorKind === "invalidType") return resolvedInvalidTypeMessage;
    if (localErrorKind === "invalidSize") return resolvedInvalidSizeMessage;
    return "";
  }, [
    localErrorKind,
    resolvedInvalidTypeMessage,
    resolvedInvalidSizeMessage,
  ]);

  const hasPreview = Boolean(preview || file);
  const selectedLabel = getPreviewLabel(file, preview, previewLabel);
  const isImagePreview =
    typeof preview === "string" &&
    (preview.startsWith("data:image") || /^https?:\/\//.test(preview));
  const isPdfFile = Boolean(
    !isImagePreview &&
      (file?.type === "application/pdf" ||
        (typeof preview === "string" &&
          preview.startsWith("data:application/pdf")) ||
        /\.pdf$/i.test(selectedLabel ?? "")),
  );
  const isImageFile = Boolean(
    file?.type?.startsWith("image/") ||
      (typeof preview === "string" &&
        (preview.startsWith("data:image") || /^https?:\/\//.test(preview))) ||
      /\.(jpe?g|png|gif|webp|svg)$/i.test(selectedLabel ?? ""),
  );
  const showUploadSuccess = Boolean(uploadCompleted && hasPreview);
  const showSelectedFilePreview =
    !showUploadSuccess && hasPreview && variant !== "logo";

  const handleRemove = showRemove
    ? () => {
        clearError();
        onChange(null, null);
      }
    : undefined;

  const reportError = (kind) => {
    setLocalErrorKind(kind);
    const message =
      kind === "invalidType"
        ? resolvedInvalidTypeMessage
        : resolvedInvalidSizeMessage;
    onError?.(message);
  };

  const clearError = () => {
    setLocalErrorKind(null);
    onError?.(null);
  };

  const selectFile = () => {
    if (!busy) inputRef.current?.click();
  };

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    const typeAllowed =
      (!resolvedAcceptedTypes?.length ||
        resolvedAcceptedTypes.includes(selectedFile.type)) &&
      fileMatchesAccept(selectedFile, resolvedAccept);

    if (!typeAllowed) {
      reportError("invalidType");
      return;
    }

    if (resolvedMaxSizeBytes && selectedFile.size > resolvedMaxSizeBytes) {
      reportError("invalidSize");
      return;
    }

    clearError();
    const reader = new FileReader();
    reader.onload = (event) => {
      onChange(selectedFile, event.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const preventDragDefaults = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const uploadClassName = [
    "ui-upload",
    `ui-upload--${variant}`,
    busy ? "ui-upload--disabled" : "",
    error || localErrorKind ? "ui-upload--error" : "",
    hasPreview ? "ui-upload--has-preview" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <UIFieldShell
      id={inputId}
      label={label}
      optionalLabel={optionalLabel}
      required={required}
      error={Boolean(error || localErrorKind)}
      helperText={
        localErrorMessage && errorAction ? (
          <>
            {localErrorMessage} {errorAction}
          </>
        ) : (
          localErrorMessage || helperText
        )
      }
    >
      <Box className={uploadClassName}>
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="file"
          accept={resolvedAccept}
          disabled={busy}
          className="ui-upload__input"
          onChange={(event) => {
            handleFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />

        {isUploading ? (
          <UIUploadProgress
            fileName={selectedLabel}
            preview={preview}
            isPdf={isPdfFile}
            progress={uploadProgress}
            label={uploadProgressLabel}
            onRemove={handleRemove}
          />
        ) : showUploadSuccess ? (
          <UIUploadedFile
            fileName={selectedLabel || uploadText}
            successLabel={successLabel}
            onRemove={handleRemove}
          />
        ) : showSelectedFilePreview ? (
          <UIUploadSelectedFile
            fileName={selectedLabel}
            preview={preview}
            isPdf={isPdfFile}
            isImage={isImageFile && !isPdfFile}
            hasError={Boolean(error || localErrorKind)}
            removeAriaLabel={removeAriaLabel}
            onRemove={handleRemove}
          />
        ) : (
          <Box
            role="button"
            tabIndex={busy ? -1 : 0}
            aria-disabled={busy}
            className="ui-upload__surface"
            onClick={selectFile}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                selectFile();
              }
            }}
            onDragEnter={preventDragDefaults}
            onDragOver={preventDragDefaults}
            onDrop={(event) => {
              preventDragDefaults(event);
              if (!busy) handleFile(event.dataTransfer.files?.[0]);
            }}
          >
          {variant === "logo" && isImagePreview && (
            <Box
              className="ui-upload__logo-preview"
              sx={{ backgroundImage: `url(${preview})` }}
            />
          )}

          {variant === "logo" && hasPreview ? (
            <Typography className="ui-upload__change-text">
              {changeText}
            </Typography>
          ) : (
            <Box className="ui-upload__empty">
              {isDropzone ? (
                <span className="ui-upload__icon-wrap" aria-hidden="true">
                  <CloudUploadOutlined />
                </span>
              ) : (
                <Upload
                  className="ui-upload__icon"
                  aria-hidden
                  strokeWidth={1.5}
                />
              )}
              {isDropzone ? (
                <p className="ui-upload__line">
                  <span className="ui-upload__text">{uploadText}</span>
                  {dragHint ? (
                    <span className="ui-upload__drag-hint"> {dragHint}</span>
                  ) : null}
                </p>
              ) : (
                <Typography className="ui-upload__text">{uploadText}</Typography>
              )}
              {hint ? (
                <Typography className="ui-upload__hint">{hint}</Typography>
              ) : null}
            </Box>
          )}
          </Box>
        )}

        {hasPreview && variant === "logo" && showRemove && !busy && (
          <IconButton
            type="button"
            aria-label="Remove file"
            className="ui-upload__remove"
            onClick={(event) => {
              event.stopPropagation();
              clearError();
              onChange(null, null);
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        )}

      </Box>
    </UIFieldShell>
  );
};

UIUpload.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.node,
  optionalLabel: PropTypes.node,
  required: PropTypes.bool,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  accept: PropTypes.string,
  acceptedTypes: PropTypes.arrayOf(PropTypes.string),
  maxSizeBytes: PropTypes.number,
  maxSizeLabel: PropTypes.string,
  invalidTypeMessage: PropTypes.string,
  invalidSizeMessage: PropTypes.string,
  uploadText: PropTypes.node,
  changeText: PropTypes.node,
  hint: PropTypes.node,
  dragHint: PropTypes.node,
  variant: PropTypes.oneOf(["logo", "dropzone"]),
  preview: PropTypes.string,
  previewLabel: PropTypes.string,
  file: PropTypes.object,
  showRemove: PropTypes.bool,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  uploadProgressLabel: PropTypes.node,
  uploadCompleted: PropTypes.bool,
  successLabel: PropTypes.node,
  removeAriaLabel: PropTypes.string,
  className: PropTypes.string,
  /** Shown beside the rejected-file message (e.g. a "Try another file" action). */
  errorAction: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

UIUpload.defaultProps = {
  id: undefined,
  name: undefined,
  label: null,
  optionalLabel: null,
  required: false,
  helperText: null,
  error: false,
  disabled: false,
  accept: undefined,
  acceptedTypes: undefined,
  maxSizeBytes: undefined,
  maxSizeLabel: undefined,
  invalidTypeMessage: undefined,
  invalidSizeMessage: undefined,
  uploadText: "Click to upload",
  changeText: "Change",
  hint: null,
  dragHint: null,
  variant: "dropzone",
  preview: "",
  previewLabel: "",
  file: null,
  showRemove: false,
  isUploading: false,
  uploadProgress: undefined,
  uploadProgressLabel: null,
  uploadCompleted: false,
  successLabel: "Uploaded Successfully",
  removeAriaLabel: "Remove file",
  className: "",
  errorAction: null,
  onError: undefined,
};

export default UIUpload;
